<template>
  <case-detail-base :case-data="caseData"></case-detail-base>
</template>

<script>
import CaseDetailBase from './CaseDetailBase.vue'

export default {
  name: 'CaseRemoteDesk',
  components: {CaseDetailBase},
  data() {
    return {
      // ===== id=1：FOCUS - 智能制造全栈式融合管控平台 =====
      // 图片目录：public/customerCase/case1/feat1~8.gif（第9项走内置 SVG）
      caseData: {
        id: 1,
        imgBase: '/customerCase/case1/feat',
        title: 'FOCUS - 智能制造全栈式融合管控平台',
        subtitle: '将分散在多地网点、厂区、坐席的终端纳入统一远程运维与质检平台，实现屏幕实时查看、远程操控、录音复听与自动化运维——数据全程不出域。',
        summary: '通过部署我司自研的远程桌面集中管控系统，将分布在产线静音房的工控机纳入统一远程运维与质检平台，实现屏幕实时查看、远程操控、录音复听、集中告警与自动化运维。原需<strong>多名</strong>操作员到现场巡检的终端，现由<strong>一人</strong>在控制台统一管控，运维人力投入显著降低；品控人员无需再逐一跑各个静音房，且 NG 音频可直接复听复判，大幅减少平均故障响应时间。',
        tags: ['WebRTC 低延迟传输', '私有化部署', '数据不出域', '跨平台桌面端', '无人值守常驻'],
        caseLabel: '客户案例',
        caseMainTitle: '从"人跑现场"到"统一管控"',
        caseMainDesc: '部署后，运维模式与合规能力发生根本性改变。',
        challengesTitle: '上线前的痛点',
        challenges: ['终端分散，运维靠"人跑"', '无统一视图，状态不可见', '操作难追溯、质检难落地', '重复劳动多，无人值守弱'],
        solutionsTitle: '我们的交付',
        solutions: ['主控端控制台 + 被控端程序', '中央服务集群私有化部署', '实时控制 + 集中监控', '录音复听 + 操作审计闭环'],
        resultsTitle: '方案收益',
        results: ['运维从静音房转为统一管控', '操作全程留痕，合规增强', '批量自动化替代重复人工', '团队精力聚焦核心业务'],
        archNodes: [
          {title: '主控端（控制台）', sub: '管理员电脑 · 桌面应用'},
          {title: '中央服务集群', sub: '信令 / TURN / 注册 / 下发'},
          {title: '被控端（每台终端）', sub: '工控机 / 坐席 / 前台 · 常驻'},
        ],
        archNote: '整套系统支持<strong>完全私有化部署</strong>于内网，所有信令、媒体流与业务数据均留在域内，满足安全合规要求。',
        archTags: [
          {abbr: 'WebRTC', desc: '点对点低延迟传输'},
          {abbr: 'Leader 选举', desc: '多连接协同'},
          {abbr: '分块续传', desc: '大文件不丢数据'},
          {abbr: '会话留痕', desc: '后端自愈兜底'},
          {abbr: '跨平台', desc: '托盘 / 自启 / 常驻'},
        ],
        features: [
          {
            title: '实时远程控制',
            desc: '主控端即可查看并操控任意终端屏幕，鼠标键盘指令端到端低延迟送达，画面跟手流畅。'
          },
          {title: '多设备集中监控', desc: '仪表盘统一呈现全部终端在线状态、CPU/内存负载与告警，一屏掌握全局。'},
          {title: '屏幕录制与复听', desc: '支持 WAV 录音回放与波形播放，配合屏幕快照，构建完整操作留痕与质检闭环。'},
          {title: '文件互传与剪贴板', desc: '主控与被控之间安全互传文件、同步剪贴板，告别 U 盘与第三方工具。'},
          {title: '批量自动化运维', desc: '批量截屏、批量消息下发、定时任务编排，把重复劳动自动化，释放人力。'},
          {title: '操作日志与审计', desc: '全量操作日志可追溯、可检索，满足合规审计要求，纠纷取证有依据。'},
          {title: '智能告警', desc: 'CPU/内存超阈值自动告警，主动发现隐患设备，避免小问题拖成大故障。'},
          {title: '无人值守', desc: '被控端开机自启、自动共享屏幕、托盘常驻，断线自动重连，真正"装好即忘"。'},
          {title: '私有化部署', desc: '全部组件部署于内网，数据不出域，满足等保与行业安全合规要求。'},
        ],
        techs: [
          {
            title: 'WebRTC 点对点低延迟传输',
            desc: '屏幕流与控制指令走 P2P 直连，跨网段通过中继穿透，画面跟手、操控流畅。'
          },
          {
            title: 'Leader 选举多连接协同',
            desc: '控制台多页面同时打开时，自动选举唯一主连接负责实时链路，避免争抢资源。'
          },
          {title: '大文件分块续传', desc: '文件传输采用分块 + 断点续传机制，弱网/中断后可恢复，不丢数据。'},
          {
            title: '会话全程留痕 + 后端自愈',
            desc: '每次远程连接起止时间、时长自动记录，具备后端自愈兜底确保审计数据不丢失。'
          },
          {title: '跨平台桌面端', desc: '主控端与被控端均为 Electron 桌面应用，支持系统托盘、开机自启与后台常驻。'},
          {
            title: '全栈技术栈成熟稳定',
            desc: 'Vue + Electron 前端、WebRTC/PeerJS 实时通信、Spring Boot 后端、Redis 缓存。'
          },
        ],
        info: {
          deliver: '主控端控制台 + 被控端程序 + 中央服务集群 + 业务后端',
          scene: '多产线、多网点终端的统一远程运维与质检管控，适用于需将分散设备纳入同一融合管控平台的制造企业',
          security: '完全私有化部署，信令/媒体流/业务数据全程不出域，满足等保合规',
          deploy: '私有化内网一键部署（数据不出域）',
          cycle: '标准场景 2 周内完成部署上线',
          service: '7×24 技术支持 / 驻场交付 / 培训赋能',
        },
        cta: {
          title: '想看看它如何运转？',
          desc: '预约一次在线演示，我们用真实控制台为你展示远程控制、集中监控与质检闭环。'
        },
      },
    };
  },
};
</script>
